import { useMemo, useState } from 'react';

import type { OrganizationMember } from '@cartwhl/common/types';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import { input } from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import TextNew from '@cartwhl/ui/base/TextNew';
import ChevronDropdownIcon from '@cartwhl/ui/icons/ChevronDropdownIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import SearchIcon from '@cartwhl/ui/icons/SearchIcon';

import { buildAssetPeopleOptions } from '../assetLibraryFilters';
import { stopAssetLibraryPanelWheelPropagation } from '../assetLibraryGridLayout';
import AssetLibraryCreatorAvatar from './AssetLibraryCreatorAvatar';

const FILTER_SELECT_INACTIVE_CLASS_NAME =
  'bg-secondary hover:bg-gray-30 dark:hover:bg-gray-60 w-full min-w-0 border-0';
const FILTER_SELECT_ACTIVE_CLASS_NAME =
  'bg-blue/10 hover:bg-blue/15 dark:bg-blue/20 dark:hover:bg-blue/30 w-full min-w-0 border-0';
const FILTER_CLEAR_BUTTON_CLASS_NAME =
  'bg-blue/10 hover:bg-blue/15 dark:bg-blue/20 dark:hover:bg-blue/30 flex h-10 w-10 flex-none items-center justify-center rounded-lg transition-colors';
const PEOPLE_DROPDOWN_WIDTH_PX = 280;

export interface AssetLibraryPeopleFilterProps {
  embedded?: boolean;
  widthClass: string;
  value?: string;
  showClear: boolean;
  organizationMembers: readonly OrganizationMember[] | undefined;
  onValueChange: (userID: string) => void;
  onClear: () => void;
}

export default function AssetLibraryPeopleFilter({
  embedded = false,
  widthClass,
  value,
  showClear,
  organizationMembers,
  onValueChange,
  onClear,
}: AssetLibraryPeopleFilterProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const peopleOptions = useMemo(
    () => buildAssetPeopleOptions(organizationMembers),
    [organizationMembers],
  );
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredPeopleOptions = useMemo(() => {
    if (!normalizedSearchQuery) {
      return peopleOptions;
    }

    return peopleOptions.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearchQuery),
    );
  }, [normalizedSearchQuery, peopleOptions]);
  const selectedOption = peopleOptions.find((option) => option.value === value);
  const triggerClassName = showClear
    ? FILTER_SELECT_ACTIVE_CLASS_NAME
    : FILTER_SELECT_INACTIVE_CLASS_NAME;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setSearchQuery('');
    }
  };

  return (
    <div className="flex w-full min-w-0 flex-none items-center gap-1 sm:w-auto">
      <div className={`min-w-0 flex-1 sm:flex-none ${widthClass}`}>
        <Dropdown
          open={open}
          onOpenChange={handleOpenChange}
          inModal={embedded}
          align="start"
          width={PEOPLE_DROPDOWN_WIDTH_PX}
          trigger={
            <button
              type="button"
              aria-label="Filter by person"
              className={input({
                className: `flex h-10 items-center justify-between gap-2 ${triggerClassName}`,
              })}
            >
              {selectedOption ? (
                <span className="flex min-w-0 items-center gap-2">
                  <AssetLibraryCreatorAvatar
                    userID={selectedOption.value}
                    organizationMembers={organizationMembers}
                    size={5}
                  />
                  <Text variant="studio/body/medium/sm" className="truncate">
                    {selectedOption.label}
                  </Text>
                </span>
              ) : (
                <Text variant="studio/body/medium/sm" className="text-gray-45">
                  People
                </Text>
              )}
              <ChevronDropdownIcon className="text-gray-45 dark:text-gray-60 relative left-1 shrink-0" />
            </button>
          }
        >
          <div className="flex flex-col gap-2 p-1">
            <label
              className="bg-secondary flex h-10 items-center gap-2 rounded-lg px-3"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <span className="sr-only">Search people</span>
              <SearchIcon className="text-primary size-4 flex-none" />
              <TextNew
                as="input"
                token="14/medium"
                type="search"
                value={searchQuery}
                placeholder="Search people"
                className="placeholder:text-tertiary text-primary min-w-0 flex-1 bg-transparent outline-none"
                onChange={(event) => setSearchQuery(event.currentTarget.value)}
                onKeyDown={(event) => event.stopPropagation()}
              />
            </label>
            <div
              className="styled-scrollbar max-h-64 overflow-y-auto"
              onWheel={stopAssetLibraryPanelWheelPropagation}
            >
              {filteredPeopleOptions.length > 0 ? (
                filteredPeopleOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="hover:bg-gray-15 dark:hover:bg-gray-90 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors"
                    onClick={() => {
                      onValueChange(option.value);
                      handleOpenChange(false);
                    }}
                  >
                    <AssetLibraryCreatorAvatar
                      userID={option.value}
                      organizationMembers={organizationMembers}
                      size={5}
                    />
                    <Text variant="studio/body/medium/sm" className="truncate">
                      {option.label}
                    </Text>
                  </button>
                ))
              ) : (
                <Text
                  variant="studio/body/medium/sm"
                  className="text-gray-45 px-3 py-2"
                >
                  No people found.
                </Text>
              )}
            </div>
          </div>
        </Dropdown>
      </div>
      {showClear ? (
        <button
          type="button"
          aria-label="Clear People filter"
          className={FILTER_CLEAR_BUTTON_CLASS_NAME}
          onClick={onClear}
        >
          <CrossIcon className="text-primary size-5" />
        </button>
      ) : null}
    </div>
  );
}
